import { CardBase } from '@sb1/ffe-cards-react';
import InputAmount from '../../../payment/input-amount/InputAmount';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatMoneyKr, getSearchParamsUrlFromObject } from '../../../../utils';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from '../../../../components/hooks/debounce';
import { getFormActions } from '../../../../utils/form-helpers';
import paymentFormConfig from '../../../../payment/payment-form-config';
import vippsFormConfig from '../../../../payment/vipps-form-config';
import { SecondaryButton, TertiaryButton } from '@sb1/ffe-buttons-react';
import { Link } from 'react-router';
import { DividerLine, SmallText } from '@sb1/ffe-core-react';
import { Tooltip } from '@sb1/ffe-form-react';
import { projectHasGiverFee } from '../../../project-model';
import { GiverprisModal } from '../../../payment/give-support/GiverprisModal';
import { validatePaymentAmount } from '../../../payment/payment-methods/paymentMethodUtils';
import { getVippsGiveMaxAmount } from '../../../../payment/payment-selectors';
import { PAYMENT_METHOD_VIPPS } from '../../../payment/payment-methods/PaymentMethods';
import VisibilitySensor from '../../../../components/visibility-sensor/VisibilitySensor';
import {
    getPropertiesForProjectABCTest,
    trackEvent,
    trackPixelEvent,
} from '../../../../support-and-tracking/analytics-actions';
import shareIcon from '@sb1/ffe-icons/icons/open/300/md/share.svg';
import { initiateVippsPayment } from '../../../../payment/vipps-actions';
import I18nText from '../../../i18n/I18nText';
import {
    PAY_OTHER_OPTIONS_TEXT_KEY,
    PAY_WITH_TEXT_KEY,
    SHARE_PROJECT_HELP_TEXT_KEY,
    SHARE_PROJECT_TEXT_KEY,
} from '../../../i18n/project-i18n-model';
import VippsButton from '../../../../components/button/vipps-button/VippsButton';
import styles from './VippsPaymentForm.module.less';
import { useProjectABCTest } from '../../../../components/hooks/projectAbcTest';
import ScreenReaderOnly from '../../../../accessibility/ScreenReaderOnly';
import { Icon } from '@sb1/ffe-icons-react';

const paymentFormActions = getFormActions(paymentFormConfig);
const vippsFormActions = getFormActions(vippsFormConfig);

export const VippsPaymentForm = ({ project, onVisibilityChange, projectShareModalRef }) => {
    const dispatch = useDispatch();
    const paymentForm = useSelector((state) => state.paymentForm);
    const vippsForm = useSelector((state) => state.vippsForm);
    const paymentConfig = useSelector((state) => state.paymentConfig);
    const [amountError, setAmountError] = useState(null);
    const debouncedAmount = useDebounce(paymentForm?.amount, 1000);
    const minAmount = project?.profile?.giveMinAmount || paymentConfig?.giveMinAmount;
    const errors = useSelector((state) => state.projects.errors);
    const maxAmount = paymentConfig?.giveMaxAmount;
    const stripeMaxAmount = useSelector((state) => state.paymentConfig.stripeGiveMaxAmount);
    const hasGiverFee = projectHasGiverFee(project);
    const modalRef = useRef(undefined);
    const vippsMaxAmount = useSelector((state) => getVippsGiveMaxAmount(state));

    const abcTest = useProjectABCTest({ project });

    const totalSum = useMemo(
        () => (paymentForm?.amount || 0) + (project?.staticGiverFee || 0),
        [paymentForm?.amount, project?.staticGiverFee]
    );
    const totalSumWithoutGiverFee = useMemo(() => paymentForm?.amount || 0, [paymentForm?.amount]);

    const setAmount = (amount) => {
        dispatch(paymentFormActions.setFormField('amount', amount));
    };

    useEffect(() => {
        if (!paymentForm?.amount || paymentForm?.amount >= minAmount) {
            setAmountError('');
            return;
        }
        if (debouncedAmount && debouncedAmount < minAmount) {
            setAmountError({
                amount: `Du må gi minst ${formatMoneyKr(minAmount)} på denne spleisen`,
            });
        } else {
            setAmountError('');
        }
    }, [paymentForm?.amount, debouncedAmount, minAmount]);

    useEffect(() => {
        return () => {
            dispatch(vippsFormActions.setFormField('isProcessingPayment', false));
            dispatch(paymentFormActions.setFormField('amount', null));
        };
    }, [dispatch]);

    const validatePayment = () =>
        validatePaymentAmount({
            method: PAYMENT_METHOD_VIPPS,
            amount: totalSum,
            setAmountError,
            vippsMaxAmount,
            minAmount,
            maxAmount,
            stripeMaxAmount,
        });

    const getExtraTrackingParameters = () => ({
        ...getPropertiesForProjectABCTest({ variant: abcTest.variant, eligible: abcTest.eligible }),
    });

    const onClickPay = () => {
        const validated = validatePayment();
        if (!validated) {
            return;
        }
        trackPixelEvent('InitiateCheckout');
        trackEvent('support', 'checkout_pay', {
            method: 'Vipps-lightning-v2',
            project_id: project.id,
            amount: totalSum,
            cart: false,
            reward: 0,
            giverName: false,
            comment: false,
            commentImage: false,
            projectHasGiverFee: hasGiverFee,
            category: project.categories?.[0],
            ...getExtraTrackingParameters(),
        });
        dispatch(
            initiateVippsPayment({
                projectId: project?.id,
                amount: totalSum,
            })
        );
    };

    const onClickShare = () => {
        trackEvent('project', 'share', {
            channel: 'modal',
            project_id: project.id,
            location: 'Vipps-lightning-v2',
            projectHasGiverFee: hasGiverFee,
            category: project.categories?.[0],
            ...getExtraTrackingParameters(),
        });
        projectShareModalRef?.current?.open();
    };

    const onClickOtherOptions = () => {
        trackEvent('support', 'other_options', {
            project_id: project.id,
            location: 'Vipps-lightning-v2',
            amount: totalSumWithoutGiverFee,
            ...getExtraTrackingParameters(),
        });
    };

    const onScrolledPastChange = useCallback(
        (isScrolledPast) => onVisibilityChange(!isScrolledPast),
        [onVisibilityChange]
    );

    const onClickGiverprisInfo = () => {
        trackEvent('project', 'open_giverinfo_tooltip', {
            project_id: project.id,
            location: 'Vipps-lightning-v2',
            ...getExtraTrackingParameters(),
        });
        modalRef?.current?.open();
    };

    return (
        <div className={styles.wrapper}>
            <DividerLine className={styles.dividerLine} />
            <CardBase className={styles.card}>
                <InputAmount
                    projectProfile={project.profile}
                    errors={{ ...errors, ...amountError }}
                    onAmountChange={setAmount}
                    hideDefaultAmounts={false}
                    extraMargin={true}
                    showLabel={false}
                    amounts={[250, 550, 1200]}
                    extraFieldMessage={
                        totalSum >= minAmount &&
                        hasGiverFee && (
                            <div className={styles.cartSum}>
                                <SmallText className={styles.cartSumItem}>
                                    <span className={styles.giverprisText}>
                                        Giverpris inkl. mva <Tooltip onClick={onClickGiverprisInfo} />
                                    </span>
                                    <span>{formatMoneyKr(project.staticGiverFee)}</span>
                                </SmallText>
                            </div>
                        )
                    }
                />
                <VisibilitySensor onScrolledPastChange={onScrolledPastChange} offset={{ top: -150 }} />
                <div className={styles.buttons}>
                    <VippsButton
                        onClick={onClickPay}
                        isLoading={vippsForm?.isProcessingPayment}
                        leaderText={<I18nText textKey={PAY_WITH_TEXT_KEY} />}
                        className={styles.vippsPaymentButton}
                    />
                    <TertiaryButton
                        as={Link}
                        to={`/project/${project?.id}/payment${getSearchParamsUrlFromObject({ amount: totalSumWithoutGiverFee }, true)}`}
                        onClick={onClickOtherOptions}
                        className={styles.otherOptionsLink}
                    >
                        <I18nText textKey={PAY_OTHER_OPTIONS_TEXT_KEY} />
                    </TertiaryButton>
                </div>
            </CardBase>
            <GiverprisModal modalRef={modalRef} />
            {project?.profile?.showSharing && (
                <>
                    <SecondaryButton
                        size="lg"
                        onClick={onClickShare}
                        aria-describedby={'share-button-lightning'}
                        data-share={true}
                        className={styles.shareButton}
                        leftIcon={<Icon fileUrl={shareIcon} size="md" />}
                    >
                        <I18nText textKey={SHARE_PROJECT_TEXT_KEY} />
                    </SecondaryButton>
                    <ScreenReaderOnly id="share-button-lightning" tag={'span'}>
                        <I18nText textKey={SHARE_PROJECT_HELP_TEXT_KEY} />
                    </ScreenReaderOnly>
                </>
            )}
        </div>
    );
};
